import { DIR_DOWN, DIR_LEFT, DIR_RIGHT, DIR_UP, type Direction } from "../avatars.js";
import type { WorkstationType } from "../roles.js";
import { isPositionWalkable } from "./collision.js";
import type {
  DeskDef,
  MapData,
  PointLightDef,
  Rect,
  Vec2,
  WindowLightDef,
  ZoneDef,
} from "./types.js";

/* Minimal structural types for the subset of the Tiled JSON format we use. */

interface TiledProperty {
  name: string;
  value: unknown;
}

interface TiledTile {
  id: number;
  properties?: TiledProperty[];
  /** Collision shapes drawn in Tiled's tile collision editor (tile-local px). */
  objectgroup?: { objects?: TiledObject[] };
}

interface TiledTileset {
  firstgid: number;
  tiles?: TiledTile[];
}

interface TiledObject {
  id: number;
  name: string;
  type: string;
  class?: string;
  x: number;
  y: number;
  width: number;
  height: number;
  point?: boolean;
  polygon?: Vec2[];
  properties?: TiledProperty[];
}

interface TiledLayer {
  name: string;
  type: "tilelayer" | "objectgroup" | "group";
  width?: number;
  height?: number;
  encoding?: string;
  data?: number[] | string;
  objects?: TiledObject[];
  layers?: TiledLayer[];
  visible?: boolean;
}

export interface TiledMap {
  width: number;
  height: number;
  tilewidth: number;
  tileheight: number;
  layers: TiledLayer[];
  tilesets: TiledTileset[];
}

export class MapParseError extends Error {}

const FLIP_MASK = 0x1fffffff;
const FLIP_H = 0x80000000;
const FLIP_V = 0x40000000;
const FLIP_D = 0x20000000;

function fail(message: string): never {
  throw new MapParseError(`office map: ${message}`);
}

function props(obj: { properties?: TiledProperty[] }): Map<string, unknown> {
  const out = new Map<string, unknown>();
  for (const p of obj.properties ?? []) out.set(p.name, p.value);
  return out;
}

function facingFrom(value: unknown, fallback: Direction): Direction {
  switch (value) {
    case "down":
      return DIR_DOWN;
    case "left":
      return DIR_LEFT;
    case "right":
      return DIR_RIGHT;
    case "up":
      return DIR_UP;
    default:
      return fallback;
  }
}

function decodeLayerData(layer: TiledLayer): number[] {
  const data = layer.data;
  if (Array.isArray(data)) return data.map((n) => Number(n) | 0);
  if (typeof data === "string") {
    if ((layer.encoding ?? "csv") === "csv") {
      return data
        .split(",")
        .map((s) => s.trim())
        .filter((s) => s.length > 0)
        .map((s) => Number.parseInt(s, 10) | 0);
    }
    fail(`unsupported tile layer encoding "${layer.encoding}"`);
  }
  fail(`tile layer "${layer.name}" has no data`);
}

function flattenLayers(layers: TiledLayer[]): TiledLayer[] {
  const out: TiledLayer[] = [];
  for (const layer of layers) {
    if (layer.type === "group" && layer.layers) out.push(...flattenLayers(layer.layers));
    else out.push(layer);
  }
  return out;
}

function flattenObjects(layers: TiledLayer[]): TiledObject[] {
  const out: TiledObject[] = [];
  for (const layer of layers) {
    if (layer.type === "group" && layer.layers) out.push(...flattenObjects(layer.layers));
    else if (layer.type === "objectgroup") out.push(...(layer.objects ?? []));
  }
  return out;
}

/**
 * The tile-local rectangles of a tile's collision shapes. Polygons and
 * ellipses collide as their bounding box; points and zero-area shapes are
 * ignored.
 */
function tileShapes(tile: TiledTile): Rect[] {
  const out: Rect[] = [];
  for (const obj of tile.objectgroup?.objects ?? []) {
    if (obj.point) continue;
    let rect: Rect = { x: obj.x, y: obj.y, w: obj.width, h: obj.height };
    if (obj.polygon && obj.polygon.length >= 3) {
      const xs = obj.polygon.map((p) => obj.x + p.x);
      const ys = obj.polygon.map((p) => obj.y + p.y);
      const x = Math.min(...xs);
      const y = Math.min(...ys);
      rect = { x, y, w: Math.max(...xs) - x, h: Math.max(...ys) - y };
    }
    if (rect.w > 0 && rect.h > 0) out.push(rect);
  }
  return out;
}

/** Apply Tiled's flip flags (diagonal first, then horizontal, then vertical). */
function flipShape(r: Rect, rawGid: number, tw: number, th: number): Rect {
  let out = r;
  if (rawGid & FLIP_D) out = { x: out.y, y: out.x, w: out.h, h: out.w };
  if (rawGid & FLIP_H) out = { ...out, x: tw - out.x - out.w };
  if (rawGid & FLIP_V) out = { ...out, y: th - out.y - out.h };
  return out;
}

function toZone(obj: TiledObject): ZoneDef {
  if (obj.polygon && obj.polygon.length >= 3) {
    return {
      id: obj.name || String(obj.id),
      type: obj.type,
      shape: "polygon",
      points: obj.polygon.map((p) => ({ x: obj.x + p.x, y: obj.y + p.y })),
    };
  }
  return {
    id: obj.name || String(obj.id),
    type: obj.type,
    shape: "rect",
    rect: { x: obj.x, y: obj.y, w: obj.width, h: obj.height },
  };
}

/** The centre of a zone's bounding box. */
function zoneCenter(zone: ZoneDef): Vec2 {
  const points = zone.points ?? [
    { x: zone.rect!.x, y: zone.rect!.y },
    { x: zone.rect!.x + zone.rect!.w, y: zone.rect!.y + zone.rect!.h },
  ];
  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  return {
    x: (Math.min(...xs) + Math.max(...xs)) / 2,
    y: (Math.min(...ys) + Math.max(...ys)) / 2,
  };
}

/**
 * Parse a Tiled JSON map into the {@link MapData} used by both server and
 * client. Throws {@link MapParseError} with a clear message on anything that
 * would break the game (missing zones, duplicate desk ids, blocked seats…).
 */
export function parseMap(raw: TiledMap): MapData {
  if (!raw || typeof raw !== "object") fail("not an object");
  if (!raw.width || !raw.height) fail("missing width/height");
  if (!raw.tilewidth || !raw.tileheight) fail("missing tile size");
  if (!Array.isArray(raw.layers) || raw.layers.length === 0) fail("no layers");

  const flat = flattenLayers(raw.layers);
  const tileLayers = flat.filter((l) => l.type === "tilelayer");

  // Which global tile ids collide, from tileset tile properties. A colliding
  // tile with collision shapes blocks only those shapes, otherwise the whole tile.
  const collides = new Set<number>();
  const shapesByGid = new Map<number, Rect[]>();
  for (const ts of raw.tilesets ?? []) {
    for (const tile of ts.tiles ?? []) {
      const cp = props(tile).get("collides");
      if (cp !== true && cp !== "true") continue;
      const gid = ts.firstgid + tile.id;
      collides.add(gid);
      const shapes = tileShapes(tile);
      if (shapes.length > 0) shapesByGid.set(gid, shapes);
    }
  }

  const width = raw.width;
  const height = raw.height;
  const tw = raw.tilewidth;
  const th = raw.tileheight;
  const collision = new Uint8Array(width * height);
  const lightBlockers = new Uint8Array(width * height);
  const shapeColliders = new Map<number, Rect[]>();

  const addShapeCollider = (r: Rect) => {
    const minTx = Math.max(0, Math.floor(r.x / tw));
    const maxTx = Math.min(width - 1, Math.floor((r.x + r.w - 0.001) / tw));
    const minTy = Math.max(0, Math.floor(r.y / th));
    const maxTy = Math.min(height - 1, Math.floor((r.y + r.h - 0.001) / th));
    for (let ty = minTy; ty <= maxTy; ty++) {
      for (let tx = minTx; tx <= maxTx; tx++) {
        const cell = ty * width + tx;
        const list = shapeColliders.get(cell);
        if (list) list.push(r);
        else shapeColliders.set(cell, [r]);
      }
    }
  };

  for (const layer of tileLayers) {
    const data = decodeLayerData(layer);
    if (data.length !== width * height) {
      fail(`layer "${layer.name}" has ${data.length} cells, expected ${width * height}`);
    }
    // The ceiling only hides the room; its tiles never block movement.
    if (layer.name === "bathroom_ceiling") continue;
    for (let i = 0; i < data.length; i++) {
      const rawGid = data[i]! >>> 0;
      const gid = (rawGid & FLIP_MASK) >>> 0;
      if (gid === 0 || !collides.has(gid)) continue;
      const shapes = shapesByGid.get(gid);
      if (shapes) {
        const ox = (i % width) * tw;
        const oy = Math.floor(i / width) * th;
        for (const s of shapes) {
          const f = flipShape(s, rawGid, tw, th);
          addShapeCollider({ x: ox + f.x, y: oy + f.y, w: f.w, h: f.h });
        }
      } else {
        collision[i] = 1;
      }
      if (layer.name === "walls") lightBlockers[i] = 1;
    }
  }

  const objects = flattenObjects(raw.layers);

  const desks: DeskDef[] = [];
  const relaxZones: ZoneDef[] = [];
  let exitZone: ZoneDef | undefined;
  const spawns: Vec2[] = [];
  const coffeeMachines: Vec2[] = [];
  const pointLights: PointLightDef[] = [];
  const windowLights: WindowLightDef[] = [];
  let bathroomArea: ZoneDef | undefined;
  let radioArea: ZoneDef | undefined;

  for (const obj of objects) {
    const light = /^(desk_light|screen|lamp)_(\d+)$/.exec(obj.name);
    if (light && obj.point) {
      const kind = light[1] === "desk_light" ? "desk" : light[1] === "screen" ? "screen" : "lamp";
      pointLights.push({
        id: obj.name,
        kind,
        x: obj.x,
        y: obj.y,
        ...(kind === "lamp" ? {} : { deskId: `desk_${light[2]}` }),
      });
    }
    const objectClass = obj.class || obj.type;
    if (objectClass === "window_up" || objectClass === "window_down") {
      windowLights.push({
        id: obj.name,
        rect: { x: obj.x, y: obj.y, w: obj.width, h: obj.height },
        direction: objectClass === "window_up" ? "up" : "down",
      });
    }
    // This trigger is identified by its Tiled name, even without a class/type.
    if (obj.name === "bathroom_area") bathroomArea = toZone(obj);
    if (obj.name === "radio_area") radioArea = toZone(obj);
    const p = props(obj);
    switch (obj.type) {
      case "desk": {
        const id = (p.get("id") as string) || obj.name;
        if (!id) fail(`desk missing id at (${obj.x}, ${obj.y})`);
        const workstationType = (p.get("workstationType") as WorkstationType) ?? "desk";
        // Tiled stores rectangles by their top-left corner; the seat is the
        // centre of the rectangle drawn over the chair (or the point itself).
        const seat = { x: obj.x + (obj.width ?? 0) / 2, y: obj.y + (obj.height ?? 0) / 2 };
        const interactX = p.has("interactX") ? Number(p.get("interactX")) : seat.x;
        const interactY = p.has("interactY") ? Number(p.get("interactY")) : seat.y;
        desks.push({
          id,
          workstationType,
          seat,
          interact: { x: interactX, y: interactY },
          facing: facingFrom(p.get("facing"), DIR_UP),
        });
        break;
      }
      case "relax_zone":
        relaxZones.push(toZone(obj));
        break;
      case "exit_zone":
        if (exitZone) fail("more than one exit zone");
        exitZone = toZone(obj);
        break;
      case "spawn":
        spawns.push({ x: obj.x, y: obj.y });
        break;
      case "coffee_machine":
        coffeeMachines.push({ x: obj.x, y: obj.y });
        break;
      default:
        break;
    }
  }

  const map: MapData = {
    width,
    height,
    tileSize: raw.tilewidth,
    collision,
    shapeColliders,
    lightBlockers,
    desks,
    relaxZones,
    exitZone: exitZone ?? fail("no exit_zone object"),
    spawns,
    coffeeMachines,
    pointLights,
    windowLights,
    bathroomArea,
    radioArea,
    radioPos: radioArea ? zoneCenter(radioArea) : undefined,
    layers: tileLayers.map((l) => l.name),
  };

  if (map.relaxZones.length === 0) fail("no relax_zone object");
  if (map.spawns.length === 0) fail("no spawn point");
  if (map.desks.length === 0) fail("no desks");

  const ids = new Set<string>();
  for (const desk of map.desks) {
    if (ids.has(desk.id)) fail(`duplicate desk id "${desk.id}"`);
    ids.add(desk.id);
    if (!isPositionWalkable(map, desk.seat.x, desk.seat.y)) {
      fail(`desk "${desk.id}" seat is not walkable at (${desk.seat.x}, ${desk.seat.y})`);
    }
  }

  for (const spawn of map.spawns) {
    if (!isPositionWalkable(map, spawn.x, spawn.y)) {
      fail(`spawn at (${spawn.x}, ${spawn.y}) is not walkable`);
    }
  }

  const lightIds = new Set<string>();
  for (const light of [...pointLights, ...windowLights]) {
    if (lightIds.has(light.id)) fail(`duplicate light id "${light.id}"`);
    lightIds.add(light.id);
    if ("deskId" in light && light.deskId && !ids.has(light.deskId)) {
      fail(`light "${light.id}" references missing desk "${light.deskId}"`);
    }
  }

  return map;
}
